// Auth-gate hooks. Centralizes the "loading → check session → redirect"
// pattern that AppShell, AdminShell, Login, and Register all repeat.
//
//   useRequireUser({ redirectTo: "/login" })
//     - waits for useCurrentUser() to settle
//     - if no session, hard-redirects to `redirectTo`
//     - returns `ready: true` once the caller may render its body
//
//   useRequireAdmin({ redirectTo: "/app" })
//     - same as useRequireUser, but also requires admin status
//     - waits for the profiles.is_admin lookup to settle (admin !== "pending")
//       so a legitimate admin is never bounced during the in-flight window
//
//   useRequireAnonymous({ redirectTo: "/app" })
//     - the inverse: for Login/Register, signed-in users shouldn't see
//       the form — redirect them away. Sends admins to /admin and
//       regular users to /app.

import { useEffect } from "react";
import { useCurrentUser } from "../useCurrentUser";

type GateOpts = { redirectTo: string };

export function useRequireUser(opts: GateOpts): { ready: boolean } {
  const me = useCurrentUser();
  useEffect(() => {
    if (me.loading) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/auth/callback") return;
    if (!me.session) {
      window.location.replace(opts.redirectTo);
    }
  }, [me.loading, me.session, opts.redirectTo]);
  return { ready: !me.loading && !!me.session };
}

export function useRequireAdmin(opts: GateOpts): { ready: boolean } {
  const me = useCurrentUser();
  useEffect(() => {
    if (me.loading) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/auth/callback") return;
    if (!me.session) {
      window.location.replace(opts.redirectTo);
      return;
    }
    // Wait for the admin lookup to settle. With the discriminated
    // union, admin === "not_admin" is the single signal that the
    // user is not an admin — there's no race between two booleans.
    if (me.admin === "not_admin") {
      window.location.replace(opts.redirectTo);
    }
  }, [me.loading, me.session, me.admin, opts.redirectTo]);
  return { ready: !me.loading && !!me.session && me.admin === "admin" };
}

export function useRequireAnonymous(opts: GateOpts): { ready: boolean } {
  const me = useCurrentUser();
  useEffect(() => {
    if (me.loading) return;
    if (typeof window === "undefined") return;
    if (me.session) {
      // Wait for the admin lookup to settle (admin !== "pending")
      // so a legitimate admin doesn't get a single-frame flash of
      // /app before being routed to /admin. Without this guard,
      // sign-in lands at /app (because admin is still "pending"
      // at the moment the redirect fires) and the AppShell then
      // catches up and bounces to /admin, but in a hard-reload
      // scenario (where the AdminShell mounts first) the order
      // reverses and the admin briefly sees "not_admin" before
      // the lookup resolves, kicking them back to /app.
      if (me.admin === "pending") return;
      const target = me.admin === "admin" ? "/admin" : (opts.redirectTo);
      window.location.replace(target);
    }
  }, [me.loading, me.session, me.admin, opts.redirectTo]);
  return { ready: !me.loading && !me.session };
}

// useRequireOrgUser — same shape as useRequireAdmin but for the org
// user dashboard. The caller must be signed in and have an
// org-side profiles.role value (org_admin — post-04917 the
// 'org_member' role is no longer assigned). System admins
// (role='admin') are sent to /admin; family users are sent
// to /app. The role lookup must settle before the redirect fires
// to avoid a single-frame flash of the wrong shell.
export function useRequireOrgUser(opts: GateOpts): { ready: boolean } {
  const me = useCurrentUser();
  useEffect(() => {
    if (me.loading) return;
    if (typeof window === "undefined") return;
    if (me.role === "pending") return;
    if (!me.session) {
      window.location.replace(opts.redirectTo);
      return;
    }
    if (me.role === "admin") {
      window.location.replace("/admin");
      return;
    }
    if (me.role !== "org_admin") {
      window.location.replace("/app");
    }
  }, [me.loading, me.session, me.role, opts.redirectTo]);
  const isOrgUser = me.role === "org_admin";
  return { ready: !me.loading && !!me.session && isOrgUser };
}
